import {
  EntityLinks,
  FilterDocumentTagMatchMode,
  FilterOptionKeys,
  GoalProgressCount,
  ID,
} from '@griply/types';
import {
  FilterSectionDefinition,
  resolveFilterSections,
  taskFilterSections,
} from '@griply/utils';
import { TFunction } from 'i18next';
import { DateFormatOptions } from 'shared/types/date-format-options';
import {
  FilterDateRangeOption,
  FilterGroup,
  FilterNumberRangeOption,
  FilterSelectOption,
} from 'shared/types/filter';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';
import { WeekDays } from 'shared/types/week-days';
import { createDateFilter } from 'shared/utils/filters/create-date-filter';
import { createPriorityFilter } from 'shared/utils/filters/create-priority-filter';

import { createCompletedAtFilter } from './create-completed-at-filter';
import { createDeadlineFilter } from './create-deadline-filter';
import { createDurationFilter } from './create-duration-filter';
import { createGoalActiveFilter } from './create-goal-active-filter';
import { createGoalFilter } from './create-goal-filter';
import { createGoalImpactFilter } from './create-goal-impact-filter';
import { createGoalTypeFilter } from './create-goal-type-filter';
import { createLifeAreaFilter } from './create-life-area-filter';
import { createTagFilter } from './create-tag-filter';
import { createTypeFilter } from './create-type-filter';

export type Options = {
  t: TFunction;
  goals: Goal[];
  // Caller-supplied: without it a migrated goal resolves no parent and the goal
  // filter tree renders it as a root.
  links: EntityLinks;
  lifeAreas: LifeArea[];
  tags: Tag[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  supportedFilters?: FilterOptionKeys[];
  hideNoGoal?: boolean;
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  lifeAreaSorting?: ID[] | null;
  currentTagMatchMode?: FilterDocumentTagMatchMode;
  labelOverrides?: Partial<Record<FilterOptionKeys, string>>;
  iconOverrides?: Partial<
    Record<FilterOptionKeys, NonNullable<FilterDateRangeOption['icon']>>
  >;
  // null clears a filter's info link (the "(i)"); a string replaces it.
  infoUrlOverrides?: Partial<Record<FilterOptionKeys, string | null>>;
  sections?: FilterSectionDefinition[];
};

export const createFilters = ({
  t,
  goals,
  links,
  lifeAreas,
  tags,
  dateFormat,
  weekStartsOn,
  supportedFilters,
  hideNoGoal,
  taskCounts,
  goalProgressCounts,
  lifeAreaSorting,
  currentTagMatchMode,
  labelOverrides,
  iconOverrides,
  infoUrlOverrides,
  sections = taskFilterSections,
}: Options): FilterGroup[] => {
  const filterCreators: Partial<
    Record<
      FilterOptionKeys,
      () =>
        | FilterSelectOption[]
        | FilterDateRangeOption[]
        | FilterNumberRangeOption[]
    >
  > = {
    [FilterOptionKeys.LifeArea]: () =>
      createLifeAreaFilter(lifeAreas, { t, lifeAreaSorting }),
    [FilterOptionKeys.Goal]: () =>
      createGoalFilter(goals, {
        t,
        hideNoGoal,
        taskCounts,
        goalProgressCounts,
        links,
      }),
    [FilterOptionKeys.GoalActive]: () => [
      createGoalActiveFilter({
        t,
        dateFormat,
        weekStartsOn,
      }),
    ],
    [FilterOptionKeys.Tag]: () =>
      createTagFilter(tags, { t, currentTagMatchMode }),
    [FilterOptionKeys.Date]: () => [
      createDateFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.Deadline]: () => [
      createDeadlineFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.CompletedAt]: () => [
      createCompletedAtFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.Priority]: () => [createPriorityFilter({ t })],
    [FilterOptionKeys.Duration]: () => [createDurationFilter({ t })],
    [FilterOptionKeys.Type]: () => [createTypeFilter({ t })],
    [FilterOptionKeys.GoalImpact]: () => [createGoalImpactFilter({ t })],
    [FilterOptionKeys.GoalType]: () => [createGoalTypeFilter({ t })],
  };

  const resolvedSections = resolveFilterSections(sections, supportedFilters);

  const filters = resolvedSections
    .flatMap(({ keys }) => keys)
    .reduce<
      (FilterSelectOption | FilterDateRangeOption | FilterNumberRangeOption)[]
    >((acc, key) => {
      const creator = filterCreators[key];
      if (creator) {
        acc.push(...creator());
      }
      return acc;
    }, []);

  const overriddenFilters =
    labelOverrides || iconOverrides || infoUrlOverrides
      ? filters.map((filter) => {
          const id = filter.id as FilterOptionKeys;
          const labelOverride = labelOverrides?.[id];
          const iconOverride = iconOverrides?.[id];
          const hasInfoUrlOverride =
            !!infoUrlOverrides && id in infoUrlOverrides;
          if (!labelOverride && !iconOverride && !hasInfoUrlOverride) {
            return filter;
          }
          return {
            ...filter,
            ...(labelOverride ? { label: labelOverride } : {}),
            ...(iconOverride ? { icon: iconOverride } : {}),
            ...(hasInfoUrlOverride
              ? { infoUrl: infoUrlOverrides?.[id] ?? undefined }
              : {}),
          };
        })
      : filters;

  // A supported key without a creator (Deleted) leaves its section empty.
  return resolvedSections.reduce<FilterGroup[]>((acc, section) => {
    const sectionFilters = overriddenFilters.filter((filter) =>
      section.keys.includes(filter.id as FilterOptionKeys),
    );

    if (!sectionFilters.length) {
      return acc;
    }

    acc.push({
      id: section.id,
      title: t(section.titleKey),
      filters: sectionFilters,
    });
    return acc;
  }, []);
};
